import React from 'react';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { SharedDialog } from '../../../common/SharedDialog';
import { surveysApi } from './api/surveysApi';
import validationConfig from '../../../validation-config.json';

const vc = validationConfig.REACTIVATE_SURVEY_DIALOG;

interface ReactivateSurveyDialogProps {
  isOpen: boolean;
  survey: { surveyId: number; name: string } | null;
  onClose: () => void;
  onReactivated: () => void;
}

const FieldError = styled(Text, {
  color: '#c0392b',
  fontSize: '0.8rem',
  margin: 0,
});

function isFutureDate(value: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const parsed = new Date(value);
  return !isNaN(parsed.getTime()) && parsed > today;
}

export const ReactivateSurveyDialog = ({
  isOpen,
  survey,
  onClose,
  onReactivated,
}: ReactivateSurveyDialogProps) => {
  const [endDate, setEndDate] = React.useState('');
  const [endDateError, setEndDateError] = React.useState<string | undefined>();
  const [endDateTouched, setEndDateTouched] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setEndDate('');
      setEndDateError(undefined);
      setEndDateTouched(false);
      setSaving(false);
      setServerError(null);
    }
  }, [isOpen]);

  const validateEndDate = (value: string): string | undefined => {
    if (!value.trim()) return vc.endDate.errorMessage;
    if (!isFutureDate(value)) return vc.endDate.mustBeFuture;
    return undefined;
  };

  const handleBlur = () => {
    setEndDateTouched(true);
    setEndDateError(validateEndDate(endDate));
  };

  const handleConfirm = async () => {
    setEndDateTouched(true);
    const error = validateEndDate(endDate);
    setEndDateError(error);
    if (error || !survey) return;

    setSaving(true);
    setServerError(null);
    try {
      await surveysApi.reactivateSurvey(survey.surveyId, endDate.trim());
      onReactivated();
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = endDate.trim() !== '' && isFutureDate(endDate);

  return (
    <SharedDialog
      mode="confirm"
      ariaLabel="Reactivate RFI"
      isOpen={isOpen}
      onClose={onClose}
      title="Reactivate RFI"
      description={
        survey
          ? `Set a new end date for "${survey.name}" to reactivate it. The end date must be in the future.`
          : undefined
      }
      size="sm"
      primaryAction={{
        label: saving ? 'Reactivating…' : 'Reactivate',
        disabled: saving || !isFormValid,
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: 'Cancel',
        variant: 'outline',
        disabled: saving,
        onClick: onClose,
      }}
    >
      {serverError && <FieldError>{serverError}</FieldError>}
      <div>
        <DateInput
          id="reactivate-end-date"
          label="New End Date (YYYY-MM-DD) *"
          dateFormat="YYYY-MM-DD"
          minDate={dayjs().startOf('day')}
          value={endDate}
          onChange={(e) => {return setEndDate(e.target.value)}}
          onBlur={handleBlur}
          isDisabled={saving}
          errorMessage={endDateTouched && endDateError ? endDateError : undefined}
        />
      </div>
    </SharedDialog>
  );
};
